import Papa from 'papaparse';
import { parse as parseSDF } from 'sdf-parser';

import { UserActions } from 'BMapsCmds';
import { App } from 'BMapsSrc/BMapsApp';
import { FoldingDataImportCase, PropApplicability } from 'BMapsSrc/model';
import { updateLocalJobState } from 'BMapsSrc/redux/jobs/access';
import { Directionality, preparePropertyGroupMetadata } from 'BMapsSrc/integrations/properties_common';
import { makeJobRequest } from './jobs_protocol';

/**
 * @typedef {'succeeded' | 'running' | 'failed' | 'starting' | 'unknown'} JobStatusOptions
 * @type {{
 *    SUCCEEDED: "succeeded",
 *    RUNNING: "running",
 *    FAILED: "failed",
 *    STARTING: "starting",
 *    UNKNOWN: "unknown"
 * }}
 */
export const JobStatus = {
    SUCCEEDED: 'succeeded',
    RUNNING: 'running',
    FAILED: 'failed',
    STARTING: 'starting',
    UNKNOWN: 'unknown',
};

/**
 * @typedef {'folding' | 'maplight' | 'gnina_scoring' | 'reinvent'} JobTypeOptions
 *  @type {{
 *    FOLDING: "folding",
 *    MAPLIGHT: "maplight",
 *    GNINA_SCORING: "gnina_scoring",
 *    REINVENT: "reinvent"
 * }}
 */
export const JobTypes = {
    FOLDING: 'folding',
    MAPLIGHT: 'maplight',
    GNINA_SCORING: 'gnina_scoring',
    REINVENT: 'reinvent',
    GNINA_DOCKING: 'gnina_docking',
    GNINA_FLEXIBLE_DOCKING: 'gnina_flexible_docking',
};

export const jobDefinitions = {
    [JobTypes.FOLDING]: {
        jobTypeId: JobTypes.FOLDING,
        isLongRunning: true,
        loadsMacromoleculeStructure: true,
        getJobTypeName() { return 'Folding'; },
        getJobName(job) { return 'Folding Job'; },
        prepareRequest(jobrequest, stash={}) {
            const requestmeta = {
                moleculeName: stash?.mapCaseParams?.name || undefined,
                foldingProgram: jobrequest.foldingProgramId,
            };
            const requestData = makeJobRequest({
                jobtype: this.jobTypeId, jobrequest, stash, requestmeta,
            });
            return requestData;
        },
        getRequestColumns() { },
        getResponseColumns() { },
        async integrateResults(job, results, options) {
            let resultsPacket = results;
            let error;
            try {
                if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
                if (!resultsPacket) {
                    return { error: 'Empty results' };
                }
                let jobResults = resultsPacket.jobResults;
                if (typeof jobResults === 'string') {
                    // For now, we expect that the results are already an object.
                    // But parsing here let's us load job results from before this change.
                    jobResults = JSON.parse(jobResults);
                }
                const firstResult = jobResults?.find((r) => r.data);
                if (!firstResult) {
                    return { error: 'Empty results' };
                }
                const stash = resultsPacket?.stash;
                const loadOptions = { ...stash?.loadOptions, ...options?.loadOptions };
                const mapCase = new FoldingDataImportCase({
                    data: firstResult.data,
                    format: firstResult.format,
                    foldingData: {
                        foldingProgramId: resultsPacket.jobrequest.foldingProgramId,
                        foldingArgs: resultsPacket.jobrequest,
                    },
                    resultProperties: firstResult.resultProperties,
                    loadOptions,
                    ...stash?.mapCaseParams,
                });
                const { errors } = await UserActions.ChooseProtein(
                    mapCase, loadOptions, options?.dataConnection
                );
                if (errors.length > 0) {
                    error = `Failed to load the folding results into BMaps: ${errors.join('; ')}`;
                }
            } catch (ex) {
                error = 'Error loading folding results';
                console.error(error, ex);
            }
            return { error, results: resultsPacket };
        },
    },
    [JobTypes.MAPLIGHT]: {
        jobTypeId: JobTypes.MAPLIGHT,
        loadsMacromoleculeStructure: false,
        getJobTypeName() { return 'MapLight'; },
        getJobName(job) { return 'MapLight Job'; },
        prepareRequest(jobrequest, stash={}) {
            const requestData = makeJobRequest({
                jobtype: this.jobTypeId, jobrequest, stash, requestmeta: {},
            });
            return requestData;
        },
        async integrateResults(job, results, options) {
            let resultsPacket = results;
            let error;
            try {
                if (!resultsPacket) return { error: 'Empty results' };
                if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
                if (!resultsPacket.jobResults) return { error: 'Empty results' };
                const { recalculate } = resultsPacket.stash || {};
                const allCmpds = App.Workspace.getLoadedCompounds();
                for (const [structId, maplightResult] of Object.entries(resultsPacket.jobResults)) {
                    const compounds = allCmpds.filter((c) => c.getUniqueStructId() === structId);
                    const scopeName = JobTypes.MAPLIGHT;
                    for (const c of compounds) {
                        for (const [propName, value] of Object.entries(maplightResult)) {
                            if (recalculate || c.getProperty(scopeName, propName) === undefined) {
                                if (value !== undefined) {
                                    c.setProperty(scopeName, propName, value);
                                }
                            }
                        }
                    }
                }
            } catch (ex) {
                error = 'Error loading MapLight results';
                console.error(error, ex);
            }

            return { error, results: resultsPacket };
        },
    },
    [JobTypes.GNINA_SCORING]: {
        jobTypeId: JobTypes.GNINA_SCORING,
        loadsMacromoleculeStructure: false,
        getJobTypeName() { return 'Gnina Scoring'; },
        getJobName(job) { return 'Gnina Scoring Job'; },
        prepareRequest(jobrequest, stash={}) {
            const requestData = makeJobRequest({
                jobtype: this.jobTypeId, jobrequest, stash, requestmeta: {},
            });
            return requestData;
        },
        async integrateResults(job, results, options) {
            let resultsPacket = results;
            let error;
            try {
                if (!resultsPacket) return { error: 'Empty results' };
                if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
                if (!resultsPacket.jobResults) return { error: 'Empty results' };
                // Properties extracted later
            } catch (ex) {
                error = `Error loading ${this.jobTypeId} results`;
                console.error(error, ex);
            }

            return { error, results: resultsPacket };
        },
    },
    [JobTypes.REINVENT]: {
        jobTypeId: JobTypes.REINVENT,
        isLongRunning: true,
        loadsMacromoleculeStructure: false,
        loadsCompound: true,
        getJobTypeName() { return 'REINVENT 4'; },
        getJobName(job) { return 'Reinvent Job'; },
        prepareRequest(jobrequest, stash={}) {
            const requestData = makeJobRequest({
                jobtype: this.jobTypeId, jobrequest, stash, requestmeta: {},
            });
            return requestData;
        },
        async integrateResults(job, results) {
            let structures = null;
            if (results.jobResults?.results) {
                structures = Papa.parse(
                    results.jobResults.results, { header: true, dynamicTyping: true }
                ).data;
                console.log(structures);
            }
            updateLocalJobState(job.jobid, { structures });
            return { results };
        },
        idField: 'SMILES',
        dataInfo: {
            SMILES: {
                label: 'Compound Structure',
                structureValue: true,
                defaultVisible: true,
            },
            Score: {
                label: 'Score',
                description: 'Composite multi-parameter optimization score after combining all objectives and transforms.',
                unit: 'Score (0-1)',
                directionality: Directionality.ONEHUNDRED,
                range: '0.0 - 1.0',
                goodRange: '>0.7',
                defaultVisible: true,
            },
            Agent: {
                label: 'Agent',
                description: 'Model (policy) log-likelihood of generating the SMILES under the current agent (higher/less negative is better).',
                unit: 'log-likelihood (nats)',
                directionality: Directionality.MAXIMIZE,
                range: 'Unbounded (typically -50 to 0)',
                goodRange: '>-10',
                defaultVisible: true,
            },
            Prior: {
                label: 'Prior',
                description: 'Reference model (prior) log-likelihood of the SMILES; used to regularize against distributional drift.',
                unit: 'log-likelihood (nats)',
                directionality: Directionality.MAXIMIZE,
                range: 'Unbounded (typically -50 to 0)',
                goodRange: '>-10',
                defaultVisible: true,
            },
            Target: {
                label: 'Target',
                description: 'Combined objective score before final scaling (depends on your scoring function setup).',
                unit: 'Score (0-1)',
                directionality: Directionality.ONEHUNDRED,
                range: '0.0 - 1.0',
                goodRange: '>0.7',
                defaultVisible: true,
            },
            'Molecular weight': {
                label: 'Weighted Molecular Weight',
                description: 'Sum of atomic masses for the molecule.',
                unit: 'Da',
                directionality: Directionality.NOT_APPLICABLE,
                range: '50 - 1000',
                goodRange: '200 - 500',
                excludeFromTable: true,
            },
            'Number of HB acceptors (Lipinski)': {
                label: 'Weighted H-Bond Acceptors (Lipinski)',
                description: 'Count of hydrogen bond acceptors as defined by Lipinski.',
                unit: 'count',
                directionality: Directionality.NOT_APPLICABLE,
                range: '0 - 20',
                goodRange: '≤10',
            },
            'Number of HB donors (Lipinski)': {
                label: 'Weighted H-Bond Donors (Lipinski)',
                description: 'Count of hydrogen bond donors as defined by Lipinski.',
                unit: 'count',
                directionality: Directionality.NOT_APPLICABLE,
                range: '0 - 10',
                goodRange: '≤5',
            },
            'QED Score': {
                label: 'Weighted QED Score',
                description: 'Quantitative estimate of drug-likeness combining multiple physicochemical properties.',
                unit: 'Score (0-1)',
                directionality: Directionality.ONEHUNDRED,
                range: '0.0 - 1.0',
                goodRange: '>0.6',
                excludeFromTable: true,
            },
            'SA score': {
                label: 'Weighted Synthetic Accessibility',
                description: 'Heuristic estimate of ease of synthesis; lower values indicate easier synthesis.',
                unit: 'SA (1-10)',
                directionality: Directionality.MINIMIZE,
                range: '1 - 10',
                goodRange: '<6',
                excludeFromTable: true,
            },
            TPSA: {
                label: 'Weighted TPSA',
                description: 'Sum of surfaces of polar atoms; correlates with absorption, BBB penetration, and permeability.',
                unit: 'Å²',
                directionality: Directionality.NOT_APPLICABLE,
                range: '0 - 250',
                goodRange: '20 - 130',
            },
            'SlogP (RDKit)': {
                label: 'Weighted LogP (RDKit)',
                description: 'Octanol/water partition coefficient estimate (lipophilicity).',
                unit: 'logP',
                directionality: Directionality.NOT_APPLICABLE,
                range: '-2 - 8',
                goodRange: '1 - 3',
            },
            'Molecular weight (raw)': {
                label: 'Molecular Weight',
                description: 'Unnormalized molecular weight prior to any scaling or transforms.',
                unit: 'Da',
                directionality: Directionality.NOT_APPLICABLE,
                range: '50 - 1000',
                goodRange: '200 - 500',
                defaultVisible: true,
            },
            'Number of HB acceptors (Lipinski) (raw)': {
                label: 'H-Bond Acceptors (Lipinski)',
                description: 'Unnormalized acceptor count.',
                unit: 'count',
                directionality: Directionality.NOT_APPLICABLE,
                range: '0 - 20',
                goodRange: '≤10',
            },
            'Number of HB donors (Lipinski) (raw)': {
                label: 'H-Bond Donors (Lipinski)',
                description: 'Unnormalized donor count.',
                unit: 'count',
                directionality: Directionality.NOT_APPLICABLE,
                range: '0 - 10',
                goodRange: '≤5',
            },
            'SA score (raw)': {
                label: 'Synthetic Accessibility',
                description: 'Unnormalized Ertl SA score; lower is easier to synthesize.',
                unit: 'SA (1-10)',
                directionality: Directionality.MINIMIZE,
                range: '1 - 10',
                goodRange: '<6',
                defaultVisible: true,
            },
            'QED Score (raw)': {
                label: 'QED Score',
                description: 'Unnormalized QED value prior to final scaling.',
                unit: 'Score (0-1)',
                directionality: Directionality.ONEHUNDRED,
                range: '0.0 - 1.0',
                goodRange: '>0.6',
                defaultVisible: true,
            },
            'SlogP (RDKit) (raw)': {
                label: 'LogP (RDKit)',
                description: 'Unnormalized logP value prior to transforms.',
                unit: 'logP',
                directionality: Directionality.NOT_APPLICABLE,
                range: '-2 - 8',
                goodRange: '1 - 3',
                defaultVisible: true,
            },
            'TPSA (raw)': {
                label: 'TPSA',
                description: 'Unnormalized TPSA prior to transforms.',
                unit: 'Å²',
                directionality: Directionality.NOT_APPLICABLE,
                range: '0 - 250',
                goodRange: '20 - 130',
                defaultVisible: true,
            },
            // Uncomment the following to completely exclude these columns from the table altogether
            // ...['SMILES_state', 'Input_SMILES', 'Scaffold', 'step'].reduce((acc, propName) => ({
            //     ...acc, [propName]: { excludeFromTable: true },
            // }), {}),
            // In lieu of fully specifying the rest of the RDKit properties,
            // just make sure we're only including one value instead of both regular and raw.
            ...[
                'GraphLength', 'NumAtomStereoCenters', 'NumRotBond',
                'Csp3', 'Numsp', 'Numsp2', 'Numsp3',
                'NumHeavyAtoms', 'NumHeteroAtoms',
                'NumRings', 'NumAromaticRings', 'NumAliphaticRings',
            ].reduce((acc, propName) => {
                const regularTag = `${propName} (RDKit)`;
                const rawTag = `${regularTag} (raw)`;
                // These properties have the same values for regular and raw,
                // so only include raw (value before transform for scoring)
                acc[regularTag] = {
                    label: regularTag,
                    excludeFromTable: true,
                };
                acc[rawTag] = {
                    label: regularTag,
                };
                return acc;
            }, {}),
        },
    },
    [JobTypes.GNINA_DOCKING]: {
        jobTypeId: JobTypes.GNINA_DOCKING,
        loadsMacromoleculeStructure: false,
        loadsCompound: true,
        getJobTypeName() { return 'Gnina Docking'; },
        getJobName(job) { return 'Gnina Docking Job'; },
        prepareRequest(jobrequest, stash={}) {
            const requestData = makeJobRequest({
                jobtype: this.jobTypeId, jobrequest, stash, requestmeta: {},
            });
            return requestData;
        },
        async integrateResults(job, results, options) {
            let resultsPacket = results;
            let error;
            try {
                if (!resultsPacket) return { error: 'Empty results' };
                if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
                if (!resultsPacket.jobResults) return { error: 'Empty results' };
                const cmpdSmiles = resultsPacket.stash?.cmpdSmiles || {};
                const parsed = parseSDF(resultsPacket.jobResults.sdf);
                const structures = parsed.molecules.map((entry, i) => {
                    const name = entry.molfile.substring(0, entry.molfile.indexOf('\n'));
                    const smiles = cmpdSmiles[name]?.smiles || null;
                    return {
                        id: `${job.jobid}_${i}`,
                        name,
                        poseI: i + 1,
                        SMILES: smiles,
                        mol: entry.molfile,
                        affinity: entry.minimizedAffinity,
                        cnn_score: entry.CNNscore,
                        cnn_affinity: entry.CNNaffinity,
                        cnn_vs: entry.CNN_VS,
                        cnn_variance: entry.CNNaffinity_variance,
                    };
                });
                updateLocalJobState(job.jobid, { structures });
            } catch (ex) {
                error = `Error loading ${this.jobTypeId} results`;
                console.error(error, ex);
            }
            return { error, results: resultsPacket };
        },
        propertyScope: 'gnina_scoring',
        dataInfo: {
            SMILES: {
                label: 'Structure',
                structureValue: true,
                defaultVisible: true,
            },
            name: {
                label: 'Source Cmpd',
                defaultVisible: true,
            },
            poseI: {
                label: 'Pose #',
                defaultVisible: true,
            },
            // Copied from gninaProps in gnina.js
            affinity: {
                label: 'Affinity (AutoDock Score)',
                description: 'Predicted binding affinity from AutoDock Vina scoring function',
                unit: 'kcal/mol',
                directionality: Directionality.MINIMIZE,
                defaultVisible: true,
            },
            cnn_affinity: {
                label: 'Gnina Affinity (CNN)',
                description: "Gnina's prediction of binding affinity in pK units (6 = 1 µM, 9 = 1 nM)",
                unit: 'pK units',
                directionality: Directionality.MAXIMIZE,
                defaultVisible: true,
            },
            cnn_score: {
                label: 'Gnina Pose Score',
                description: "Gnina's predicted probability that the pose is within 2Å of the correct binding pose",
                unit: 'Probability',
                directionality: Directionality.ONEHUNDRED,
                defaultVisible: true,
            },
            cnn_vs: {
                label: 'Gnina VS',
                description: "Gnina's virtual screening score (Gnina affinity * Gnina pose score)",
                unit: '(composite)',
                directionality: Directionality.MAXIMIZE,
                defaultVisible: true,
            },
            cnn_variance: {
                label: 'Gnina CNN Variance',
                description: "Gnina's report of the variance/uncertainty of the CNN affinity scoring, a measure of confidence in the prediction",
                unit: '',
                directionality: Directionality.ZERO,
            },
        },
    },
};

export function lookupJobDefinition(jobTypeId) {
    return jobDefinitions[jobTypeId];
}

export const getPropertyMetadata = () => ({
    [JobTypes.REINVENT]: preparePropertyGroupMetadata({
        groupMeta: {
            propApplicability: PropApplicability.PER_COMPOUND,
            label: 'Reinvent properties',
        },
        propertyInfo: jobDefinitions[JobTypes.REINVENT].dataInfo,
        tooltipHeading: 'Reinvent properties',
    }),
});
